/**
 * Setlist context of the song page (pure — unit tested): /songs/:id?c=<collectionId>&i=<index>&e=<entryId>,
 * where e is the entry id (stable: survives a reorder and tells repeats of a song apart) and i
 * indexes the collection's entries ordered by position (as CollectionScreen links them) — the
 * fallback for older links. i differs per viewer (members get only the entries they may see).
 */
import type { CollectionEntryDTO } from '../../shared/dto/library';
import type { NextPastEndEvent, NextPastEndResult } from './env';

export interface SetlistContext {
  collectionId: string;
  index: number;
  /** The entry the link was made for (?e=); preferred over `index`. Absent on older links. */
  entryId?: string;
}

const COLLECTION_ID = /^[A-Za-z0-9-]{1,64}$/;
const ENTRY_ID = COLLECTION_ID;

/** Reads ?c=&i=&e= (a missing / invalid index counts as 0, an invalid e is ignored); null without a valid collection id. */
export function parseSetlist(params: URLSearchParams): SetlistContext | null {
  const c = params.get('c');
  if (!c || !COLLECTION_ID.test(c)) return null;
  const raw = params.get('i');
  const i = raw === null || raw.trim() === '' ? 0 : Number(raw);
  const e = params.get('e');
  return { collectionId: c, index: Number.isSafeInteger(i) && i >= 0 ? i : 0, ...(e && ENTRY_ID.test(e) ? { entryId: e } : {}) };
}

/** Entries in setlist order (position, then entry id for stable ties). */
export function orderedEntries(entries: readonly CollectionEntryDTO[]): CollectionEntryDTO[] {
  return [...entries].sort((a, b) => a.position - b.position || (a.entryId < b.entryId ? -1 : a.entryId > b.entryId ? 1 : 0));
}

/**
 * Index of the open song in the ordered entries: the entry `entryId` when it is still there with
 * that song (a reorder moves it), else `index` when that entry is the song, else the nearest entry
 * with the song (the list may have changed since the link was made; repeats are allowed — the
 * closest one wins, the earlier on a tie); -1 when the song is not in the list.
 */
export function resolveSetlistIndex(ordered: readonly CollectionEntryDTO[], songId: string, index: number, entryId?: string | null): number {
  if (entryId) {
    const k = ordered.findIndex((e) => e.entryId === entryId);
    if (k >= 0 && ordered[k]!.songId === songId) return k;
  }
  if (ordered[index]?.songId === songId) return index;
  let best = -1;
  for (let k = 0; k < ordered.length; k++) {
    if (ordered[k]!.songId !== songId) continue;
    if (best < 0 || Math.abs(k - index) < Math.abs(best - index)) best = k;
  }
  return best;
}

export interface SetlistStep {
  songId: string;
  index: number;
  entryId: string;
}

export interface SetlistNavState {
  /** 0-based index of the open song's entry. */
  index: number;
  /** The open song's entry. */
  entryId: string;
  /** 1-based position for "3 / 12". */
  position: number;
  total: number;
  prev: SetlistStep | null;
  next: SetlistStep | null;
}

/** Prev/next of the open song; null when the song is not in the list or the list has < 2 songs. */
export function setlistNav(ordered: readonly CollectionEntryDTO[], songId: string, index: number, entryId?: string | null): SetlistNavState | null {
  if (ordered.length < 2) return null;
  const i = resolveSetlistIndex(ordered, songId, index, entryId);
  if (i < 0) return null;
  const step = (k: number): SetlistStep | null => {
    const e = ordered[k];
    return e ? { songId: e.songId, index: k, entryId: e.entryId } : null;
  };
  return { index: i, entryId: ordered[i]!.entryId, position: i + 1, total: ordered.length, prev: step(i - 1), next: step(i + 1) };
}

/**
 * Query string for another song of the same setlist (c, i and e — e dropped when unknown, so a
 * stale one never wins over i), keeping any other parameters.
 */
export function setlistSearch(current: URLSearchParams, collectionId: string, index: number, entryId?: string | null): string {
  const p = new URLSearchParams(current);
  p.set('c', collectionId);
  p.set('i', String(index));
  if (entryId) p.set('e', entryId);
  else p.delete('e');
  return `?${p.toString()}`;
}

/** A second "next" opens the next song only after the "Næsta lag ›" chip has shown this long. */
export const NEXT_SONG_ARM_MS = 700;

export interface PastEndState {
  /** The setlist has a song after this one. */
  hasNext: boolean;
  /** Edit mode: "next" beyond the end does nothing. */
  editing: boolean;
  /** When the "Næsta lag ›" chip appeared (performance.now()), or null while it is hidden. */
  chipShownAt: number | null;
  now: number;
}

/**
 * What "next" beyond the last page / spread does: 'show' the "Næsta lag ›" chip; 'open' the next
 * song — only a second key / pedal press, not auto-repeated, once the chip has shown for
 * NEXT_SONG_ARM_MS (a double press or a held key never skips a song; taps and swipes only show the
 * chip, which is itself the tap target); else 'none'.
 */
export function nextPastEndAction(state: PastEndState, e: NextPastEndEvent): 'show' | 'open' | 'none' {
  if (!state.hasNext || state.editing) return 'none';
  if (state.chipShownAt === null) return 'show';
  if (e.via !== 'key' || e.repeat || state.now - state.chipShownAt < NEXT_SONG_ARM_MS) return 'none';
  return 'open';
}

/**
 * What the song page tells the viewer about `action` (SheetViewerEnv.onNextPastEnd): while a next
 * song follows (outside edit mode) "next" is always handled — the chip is up — so the viewer never
 * adds "Síðasta síða" beside it; 'none' only when nothing follows (then the hint shows).
 */
export function pastEndResult(action: 'show' | 'open' | 'none', hasNext: boolean, editing: boolean): NextPastEndResult {
  return action === 'none' && hasNext && !editing ? 'show' : action;
}

/**
 * A tap on the "Næsta lag ›" chip opens the next song only once the chip has shown for
 * NEXT_SONG_ARM_MS: the chip appears under the finger that tapped the right side of the last page,
 * so the second tap of a double tap must not skip a song (it is ignored; the chip stays up).
 */
export function nextChipArmed(chipShownAt: number, now: number): boolean {
  return now - chipShownAt >= NEXT_SONG_ARM_MS;
}
